import { useState } from "react";
import { toast } from "sonner";
import { ShieldCheck, Loader2, AlertTriangle, ExternalLink, Globe } from "lucide-react";
import { useApp } from "@/app/AppLayout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import api, { apiErr } from "@/lib/api";

export default function Similarity() {
  const { setCredits, refreshMeta } = useApp();
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);

  const run = async () => {
    if (text.trim().length < 80) { toast.error("Texte trop court (min. 80 caractères)."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/similarity", { text });
      setReport(data); setCredits(data.credits); refreshMeta();
      toast.success(`Analyse terminée (${data.cost} crédits)`);
    } catch (e) { toast.error(apiErr(e)); } finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto px-6 py-8 animate-fade-up">
      <h1 className="font-serif text-3xl font-bold text-navy">Vérification de similarité</h1>
      <p className="text-muted2 mt-1">Recherche <strong>réelle</strong> de passages similaires sur Internet, avec sources et liens vérifiables.</p>

      <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-4 flex gap-3 text-sm text-amber-900">
        <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
        <p>Une similarité élevée <strong>n'implique pas automatiquement un plagiat</strong>. Chaque source est un lien réel à vérifier vous-même. Aucun résultat n'est inventé.</p>
      </div>

      <Textarea value={text} onChange={(e) => setText(e.target.value)} rows={8} placeholder="Collez ici le passage à vérifier (min. 80 caractères)…" className="mt-4 border-[#E5E7EB] resize-none" data-testid="similarity-input" />
      <Button onClick={run} disabled={loading} className="mt-3 bg-navy text-white hover:bg-navy-800 rounded-full gap-2" data-testid="similarity-run">{loading ? <><Loader2 className="h-4 w-4 animate-spin" /> Recherche sur Internet…</> : <><ShieldCheck className="h-4 w-4" /> Vérifier</>}</Button>

      {report && (
        <div className="mt-6 bg-white rounded-2xl border border-[#E5E7EB] p-6" data-testid="similarity-report">
          <div className="flex items-center justify-between">
            <div><p className="text-sm text-muted2">Indice de similarité (sur sources trouvées)</p><p className="font-serif text-4xl font-bold text-navy">{report.score}%</p><p className="text-xs text-muted2">{report.searched} requêtes web · {report.matches.length} sources</p></div>
            <div className="w-40"><Progress value={report.score} className="h-3" /></div>
          </div>
          <div className="mt-5 space-y-3">
            {report.matches.length === 0 ? <p className="text-sm text-muted2">Aucune source similaire détectée sur Internet pour ce passage.</p> :
              report.matches.map((m, i) => (
                <div key={i} className="border border-[#E5E7EB] rounded-xl p-4" data-testid={`sim-match-${i}`}>
                  <div className="flex items-start justify-between gap-3">
                    <a href={m.url} target="_blank" rel="noreferrer" className="font-medium text-navy hover:text-lisungi-yellow flex items-start gap-1.5 min-w-0">
                      <Globe className="h-4 w-4 mt-0.5 shrink-0" /><span className="truncate">{m.title}</span><ExternalLink className="h-3 w-3 mt-1 shrink-0" />
                    </a>
                    <span className="text-sm font-semibold text-navy shrink-0">{Math.round(m.score * 100)}%</span>
                  </div>
                  <p className="text-xs text-muted2 mt-1 truncate">{m.url}</p>
                  {m.snippet && <p className="text-sm text-ink mt-2 leading-relaxed">« {m.snippet} »</p>}
                  <p className="text-[11px] text-muted2 mt-2 font-mono">Comparé à votre passage : « {m.passage} »</p>
                </div>
              ))}
          </div>
          <p className="text-xs text-muted2 mt-4">{report.note}</p>
        </div>
      )}
    </div>
  );
}
